import type {ComponentType, LazyExoticComponent} from "react";
import {lazy} from "react";
import {pronounceSafeSeq} from "@cdx/shared/sequences";
import {api} from "./lib/api";
import {waitForResultPromise} from "./lib/wait-for-result";
import {slugify} from "./lib/utils";
import {stringifyParams} from "./lib/request-utils";

export type RouteDef = {
  path: string | string[];
  getUrl: (...args: any[]) => string;
  component?: LazyExoticComponent<ComponentType<any>>;
  isPublic?: boolean;
  noDefaultLayout?: boolean;
  exact?: boolean;
  /** the route exists for `path`/`getUrl` only; App.tsx does not render it */
  doNotRender?: boolean;
  ensureUrl?: (id: string) => Promise<string>;
};

type SeqAndTitle = {accountSeq: number; title: string};

const Decks = lazy(() => import("./pages/decks"));
const Milestone = lazy(() => import("./pages/milestones"));

const cardSeqRegex = new RegExp(`^([${pronounceSafeSeq.letters}]{3,5})(?:-.*)?`);
const toCardPath = ({title, accountSeq}: SeqAndTitle) =>
  `${pronounceSafeSeq.intToSeq(accountSeq)}-${slugify(title)}`;
export const cardPathToSeq = (cardPath: string): number | null => {
  const m = cardPath.match(cardSeqRegex);
  if (m) return pronounceSafeSeq.seqToInt(m[1]);
  return null;
};

const deckSeqRegex = /^([\d]{1,5})(?:-.*)?/;
const toDeckPath = ({title, accountSeq}: SeqAndTitle) => `${accountSeq}-${slugify(title)}`;
export const deckPathToSeq = (deckPath: string): number | null => {
  const m = deckPath.match(deckSeqRegex);
  if (m) return parseInt(m[1], 10);
  return null;
};

const routes = {
  login: {
    noDefaultLayout: true,
    isPublic: true,
    path: "/login",
    getUrl: () => "/login",
    component: lazy(() => import("./pages/login")),
  },
  notAuthorised: {
    isPublic: true,
    path: "/forbidden",
    getUrl: (reason?: string) => `/forbidden${reason ? `?${stringifyParams({reason})}` : ""}`,
    component: lazy(() => import("./pages/403")),
  },
  freeLimitsExceeded: {
    isPublic: true,
    path: "/read-only",
    getUrl: () => "/read-only",
    component: lazy(() => import("./pages/402")),
  },

  join: {
    noDefaultLayout: true,
    isPublic: true,
    path: "/join/:token",
    getUrl: (token: string) => `/join/${token}`,
    component: lazy(() => import("./pages/join")),
  },
  joinViaInviteCode: {
    noDefaultLayout: true,
    isPublic: true,
    path: "/join-link/:token",
    getUrl: (token: string) => `/join-link/${token}`,
    component: lazy(() => import("./pages/join-via-invite-code")),
  },
  resetPassword: {
    noDefaultLayout: true,
    isPublic: true,
    path: "/reset-password/:token",
    getUrl: (token: string) => `/reset-password/${token}`,
    component: lazy(() => import("./pages/reset-password")),
  },
  verifyEmail: {
    noDefaultLayout: true,
    isPublic: true,
    path: "/verify-email/:token",
    getUrl: (token: string) => `/verify-email/${token}`,
    component: lazy(() => import("./pages/verify-email")),
  },

  releases: {
    isPublic: true,
    path: "/changelog",
    getUrl: () => `/changelog`,
    component: lazy(() => import("./features/releases/ReleasePage")),
  },
  error: {
    isPublic: true,
    path: "/error",
    getUrl: () => `/error`,
    component: lazy(() => import("./pages/error")),
  },

  legacyProjectRoutes: {
    noDefaultLayout: true,
    path: "/p",
    getUrl: (): string => {
      throw new Error("Deprecated");
    },
    component: lazy(() => import("./pages/legacy/legacy-project-routes")),
  },

  hand: {
    noDefaultLayout: true,
    exact: true,
    path: ["", "/", "/card/:cardId", "/metrics", "/activity", "/upvotes"],
    getUrl: () => "",
    component: lazy(() => import("./features/hand-queue/HandQueueDashboard")),
  },
  handWithCard: {
    doNotRender: true,
    path: "/card/:cardId",
    getUrl: ({accountSeq, title}: SeqAndTitle) => `/card/${toCardPath({accountSeq, title})}`,
  },
  handWithMetrics: {
    doNotRender: true,
    path: "/metrics",
    getUrl: () => "/metrics",
  },

  decks: {
    noDefaultLayout: true,
    path: "/decks",
    getUrl: () => "/decks",
    component: Decks,
  },
  deck: {
    doNotRender: true,
    path: "/decks/:deckId",
    getUrl: ({accountSeq, title}: SeqAndTitle) => `/decks/${toDeckPath({accountSeq, title})}`,
    ensureUrl: (deckId: string) =>
      waitForResultPromise(() => {
        const deck = api.getModel({modelName: "deck", id: deckId})!;
        return {title: deck.title, accountSeq: deck.accountSeq};
      }).then((deck) => `/decks/${toDeckPath(deck)}`),
  },
  deckWithCard: {
    doNotRender: true,
    path: "/decks/:deckId/card/:cardId",
    getUrl: (deck: SeqAndTitle, card: SeqAndTitle) =>
      `/decks/${toDeckPath(deck)}/card/${toCardPath(card)}`,
  },

  calendar: {
    noDefaultLayout: true,
    path: "/milestones/calendar",
    getUrl: () => "/milestones/calendar",
    component: lazy(() => import("./pages/milestones/CalendarPage")),
  },

  milestones: {
    noDefaultLayout: true,
    path: "/milestones",
    getUrl: () => "/milestones",
    component: Milestone,
  },

  // make sure future routes are also considered in 'useRouteDependentCardLink'
  dueDate: {
    doNotRender: true,
    path: "/milestones/due/:dayKey",
    getUrl: (dayKey: string) => `/milestones/due/${dayKey}`,
  },
  dueDateWithCard: {
    doNotRender: true,
    path: "/milestones/due/:dayKey/card/:cardId",
    getUrl: (dayKey: string, card: SeqAndTitle) =>
      `/milestones/due/${dayKey}/card/${toCardPath(card)}`,
  },

  milestone: {
    doNotRender: true,
    path: "/milestones/:milestoneId",
    getUrl: (milestoneAccountSeq: number) => `/milestones/${milestoneAccountSeq}`,
  },
  milestoneWithCard: {
    doNotRender: true,
    path: "/milestones/:milestoneId/card/:cardId",
    getUrl: (milestoneAccountSeq: number, card: SeqAndTitle) =>
      `/milestones/${milestoneAccountSeq}/card/${toCardPath(card)}`,
  },

  sprint: {
    doNotRender: true,
    path: "/milestones/run/:sprintId",
    getUrl: (sprintAccountSeq: number) => `/milestones/run/${sprintAccountSeq}`,
  },
  sprintWithCard: {
    doNotRender: true,
    path: "/milestones/run/:sprintId/card/:cardId",
    getUrl: (sprintAccountSeq: number, card: SeqAndTitle) =>
      `/milestones/run/${sprintAccountSeq}/card/${toCardPath(card)}`,
  },

  visionBoardEmpty: {
    noDefaultLayout: true,
    path: "/vision",
    getUrl: () => `/vision`,
    component: lazy(() => import("./features/vision-board/VisionBoardRouter")),
  },
  visionBoard: {
    doNotRender: true,
    path: "/vision/:boardId",
    getUrl: (boardSeq: number) => `/vision/${boardSeq}`,
  },

  media: {
    path: "/media",
    getUrl: () => "/media",
    component: lazy(() => import("./pages/media-library")),
  },
  timeTrackingReport: {
    path: "/time-tracking-report",
    getUrl: () => "/time-tracking-report",
    component: lazy(() => import("./features/time-tracking/TimeTrackingReport")),
  },
} satisfies Record<string, RouteDef>;

export type RouteKey = keyof typeof routes;

export default routes;
